<template>
  <v-container class="pa-0" v-if="this.group.my_role =='admin' || this.$auth.check('uber_admin')">
    <h3 class="mb-3">Manage Administrators: {{group.name}}</h3>
    <hr class='thick' />
    <p class='mt-2'>Manage group administrators below. </p>
    <ul class='my-1'>
      <li><strong>To add new administrators,</strong> begin by starting to type their name and then select them from the list.</li>
      <li><strong>To remove administrators,</strong> click the "x" next to their name.
        <ul>
          <li>When you remove an administrator, the user remains a member of the group.</li>
          <li>If you intend to remove a user from this group visit <strong><router-link replace class="manageMembers" :to="'/group/members/' + group_id" v-if="group.my_role =='admin' || this.$auth.check('uber_admin')">Manage Members</router-link></strong>.</li>
        </ul>
      </li>
      <li>Don't forget to save your changes!</li>
    </ul>
    <v-autocomplete
      v-model="members"
      :items="ac_results"
      :label="!membersLoaded ? 'Fetching Administrators' : 'Add and Remove Administrators'"
      :readonly="!membersLoaded"
      :loading="!membersLoaded"
      item-text="display_name"
      item-value="id"
      item-avatar="profile_photo.avatar"
      multiple
      chips
      cache-items
      :search-input.sync="search"
      autocomplete
      class="mt-4"
      outline
      hide-selected
      no-data-text="Start typing to search"
      :error-messages="errors"
      @change="search = ''"
    >
      <template slot="selection" slot-scope="data">
        <v-chip
          close
          @input="data.parent.selectItem(data.item)"
          :selected="data.selected"
          class="chip--select-multi"
          :key="JSON.stringify(data.item)"
        >
          <AmazeAvatar :user="data.item" :key="data.item.id" size="25px" hideTip="true"></AmazeAvatar>
          {{ data.item.display_name }}
        </v-chip>
      </template>
      <template slot="item" slot-scope="data">
        <template v-if="typeof data.item !== 'object'">
          <v-list-tile-content v-text="data.item"></v-list-tile-content>
        </template>
        <template v-else>
          <v-list-tile-content>
            <v-list-tile-title v-html="data.item.display_name"></v-list-tile-title>
          </v-list-tile-content>
        </template>
      </template>
    </v-autocomplete>

    <v-btn
      class="ml-0"
      :disabled="!dirty || errors"
      color="primary"
      @click.enter="saveGroup"
      >Save Changes</v-btn>
    <v-btn @click="returnToGroup()" class="ml-1" flat outline
      >Cancel and Close</v-btn
    >
  </v-container>
  <v-container class="pa-0" v-else>
    <h3 class="mb-3" v-if="this.group.my_role">You do not have access to this feature.</h3>
    <h3 class="mb-3" v-else>This will only take a moment &hellip;</h3>
  </v-container>
</template>

<script>
import AmazeAvatar from "@/components/Widgets/AmazeAvatar";
import GroupService from "@/services/GroupService";

export default {
  components: {
    AmazeAvatar
  },
  computed: {
    group_id() {
      return this.$route.params.id;
    }
  },
  data() {
    return {
      loading: false,
      membersLoaded: false,
      search: null,
      ac_results: [],
      group: {},
      group_types: {},
      members: [],
      dirty: false,
      errors: false
    };
  },
  watch: {
    search(val) {
      val && this.querySelections(val);
    },
    members(val) {
      // Mark the form as dirty when the members list changes.
      this.dirty = true;

      if (!val || val.length < 1) {
        this.errors = "You must have at least one group administrator.";
      } else {
        this.errors = false;
      }
    }
  },
  methods: {
    getGroupMembers: function(groupId) {
      this.membersLoaded = false;
      return GroupService.getGroupAdmins(groupId)
        .then(
          response => {
            this.ac_results = response;
            // get body data
            this.members = response.sort((a, b) => {
              const result =
                a["last_name"] == b["last_name"]
                  ? 0
                  : a["last_name"] > b["last_name"]
                    ? 1
                    : -1;
              return result !== 0
                ? result
                : a["first_name"] == b["first_name"]
                  ? 0
                  : a["first_name"] > b["first_name"]
                    ? 1
                    : -1;
            })
              .map(data => {
                return data.hasOwnProperty("id") ? data["id"] : data;
              });

            this.$nextTick(() => {
              this.dirty = false;
            });
          },
          response => {
            console.error("[error] getGroupMembers");
          }
        )
        .finally(() => {
          this.$nextTick(() => {
            this.membersLoaded = true;
          });
        });
    },
    returnToGroup() {
      if (this.group.id) {
        this.$router.push("/group/" + this.group.id);
      } else {
        this.$router.go(-1);
      }
    },
    querySelections(v) {
      this.loading = true;
      setTimeout(() => {
        this.$http.get("users/autocomplete?q=" + v).then(
          response => {
            this.ac_results = response.body;
          },
          response => {
            console.log("Error getting users from autocomplete.");
          }
        );
        this.loading = false;
      }, 300);
    },
    saveGroup() {
      var formData = {
        group_id: this.$route.params.id,
        users: this.members.map(data => { return data.hasOwnProperty('id') ? data['id'] : data })
      };

      if (!formData.users || formData.users.length < 1) {
        this.errors = "You must have at least one group administrator";
        return;
      }

      // 2021-01-26: QA ran into an issue where removing 1 user seemed to remove
      // all users from the group. We have not been able to reproduce this issue.
      // As a concession, I'm going to add a confirmation dialog in case this
      // actually can/does prevent removing all users. I've ported this over
      // to the administrator management screen. Hopefully it's not possible
      // to ever get to this situation (due to the check above). But just in case ....
      if (formData.users.length < 1 && !confirm("You have removed all administrators from this group. Are you sure you want to save this change?")) {
        return;
      }

      this.$http.post("groups/administrators/update", formData).then(
        response => {
          this.$store.commit("updateMessage", {
            message: "Members saved.",
            snackbar: true
          });
          this.$router.go(-1);
        },
        response => {
          console.log("Error saving members.");
        }
      );
    },
    retrieveGroup(groupId) {
      this.loading = true;
      this.$http
        .get("groups/" + groupId)
        .then(
          response => {
            this.group = response.body.data;
          },
          response => {
            console.log("Error getting group types.");
          }
        )
        .finally(response => {
          this.loading = false;
        });

      this.getGroupMembers(groupId);
    }
  },
  mounted() {
    const returnRoute = "/group/" + this.$route.params.id;
    this.$emit('setReturnRoute', returnRoute);
    this.retrieveGroup(this.$route.params.id);
  }
};
</script>
